import { Browser } from '@capacitor/browser'
import { CapacitorHttp } from '@capacitor/core'
import {
  SSOCallbackRequest,
  SSOCallbackResponse
} from '@epilot/customer-portal-client'
import { jwtDecode } from 'jwt-decode'
import {
  UserManager,
  User,
  WebStorageStateStore,
  UserManagerSettings,
  INavigator,
  IWindow,
  NavigateResponse,
  UserProfile
} from 'oidc-client-ts'

import { ProviderPublicConfig } from 'api/ApiTypes'
import { getConfig } from 'EnvConfig'

type WindowWithSSOUserManager = Window &
  typeof globalThis & { SSOUserManager: UserManager }
export type SSOUserManager = UserManager
export type SSOUser = User

export const getSSOUserManager = () =>
  (window as WindowWithSSOUserManager).SSOUserManager

export const setSSOUserManager = (userManager: SSOUserManager) => {
  ;(window as WindowWithSSOUserManager).SSOUserManager = userManager
}

export const getSSOUser = async () => {
  const userManager = getSSOUserManager()

  if (userManager) {
    const user = await userManager.getUser()

    return user
  }

  return null
}

export interface PlatformType {
  platform: 'web' | 'ios' | 'android'
}

export function getPlatformConfig(): PlatformType {
  const isMobile =
    !!(window as any).Capacitor?.isNativePlatform &&
    ((window as any).Capacitor.getPlatform?.() === 'ios' ||
      (window as any).Capacitor.getPlatform?.() === 'android')

  if (isMobile) {
    return {
      platform: (window as any).Capacitor.getPlatform?.()
    }
  } else {
    return {
      platform: 'web'
    }
  }
}

class CapacitorNavigator implements INavigator {
  async prepare(): Promise<IWindow> {
    return {
      ...window,
      navigate: async (params: { url: string }) => {
        // Delegate to our custom navigate implementation
        await this.navigate(params)

        return { url: params.url }
      }
    }
  }

  async navigate(params: { url: string }): Promise<NavigateResponse> {
    if (
      getPlatformConfig().platform === 'ios' ||
      getPlatformConfig().platform === 'android'
    ) {
      try {
        await Browser.open({ url: params.url, presentationStyle: 'popover' })
      } catch (error) {
        console.error('Browser.open failed:', error)
      }
    } else {
      window.location.assign(params.url)
    }

    return { url: params.url }
  }

  async callback(): Promise<void> {
    try {
      await Browser.close()
    } catch (error) {
      console.error('Error closing browser:', error)
    }
  }
}

const AllPostBasedProviders = ['apple']

export const isPostBasedRedirect = (slug: string) => {
  return AllPostBasedProviders.includes(slug)
}

export const isImplicitFlowHasSecret = (
  providerConfig: ProviderPublicConfig
) => {
  return (
    providerConfig?.oidc_config?.type === 'implicit' &&
    providerConfig?.oidc_config?.has_client_secret
  )
}

const initializeWebManager = (providerConfig: ProviderPublicConfig) => {
  const isPostBased = isPostBasedRedirect(providerConfig.slug)

  // @ts-expect-error It is supported but not recognized in types
  const managerConfig: UserManagerSettings = {
    client_id: providerConfig?.oidc_config?.client_id,
    ...(providerConfig?.oidc_config?.client_secret
      ? { client_secret: providerConfig?.oidc_config?.client_secret }
      : {}),
    authority: providerConfig?.oidc_config?.oidc_issuer,
    redirect_uri: providerConfig?.oidc_config?.redirect_uri,
    response_type: 'code',
    scope: providerConfig?.oidc_config?.scope,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    ...(providerConfig.slug === 'apple'
      ? { metadata: providerConfig?.oidc_config?.metadata }
      : undefined),
    ...(isPostBased ? { response_mode: 'form_post' } : {}),
    extraTokenParams: {
      slug: providerConfig.slug,
      token_endpoint: providerConfig?.oidc_config?.metadata?.token_endpoint
    },
    ...(providerConfig.oidc_config?.prompt
      ? { prompt: providerConfig.oidc_config.prompt }
      : {})
  }

  const userManager = new UserManager(managerConfig)

  setSSOUserManager(userManager)
}

const initializeMobileManager = (providerConfig: ProviderPublicConfig) => {
  const managerConfig: UserManagerSettings = {
    client_id: providerConfig?.mobile_oidc_config?.client_id,
    ...(providerConfig?.mobile_oidc_config?.client_secret
      ? { client_secret: providerConfig?.mobile_oidc_config?.client_secret }
      : {}),
    authority: providerConfig?.oidc_config?.oidc_issuer,
    redirect_uri: providerConfig?.oidc_config?.metadata?.mobile_redirect_uri,
    response_type: 'code',
    scope: providerConfig?.oidc_config?.scope,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    ...(providerConfig.slug === 'apple'
      ? { metadata: providerConfig?.oidc_config?.metadata }
      : undefined),
    redirectMethod: 'replace' as const,
    redirectTarget: 'self' as const,
    monitorSession: false,
    automaticSilentRenew: false,
    extraTokenParams: {
      slug: providerConfig.slug,
      token_endpoint: providerConfig?.oidc_config?.metadata?.token_endpoint
    }
  }

  const userManager = new UserManager(managerConfig, new CapacitorNavigator())

  setSSOUserManager(userManager)
}

export const initializeSSOUserManager = (
  providerConfig: ProviderPublicConfig
) => {
  if (!providerConfig) return

  const platformConfig = getPlatformConfig()
  const isMobile =
    platformConfig.platform === 'ios' || platformConfig.platform === 'android'

  if (isMobile) {
    initializeMobileManager(providerConfig)
  } else {
    initializeWebManager(providerConfig)
  }
}

export const getCurrentOidcSession = async (
  userStore: UserManager['settings']['userStore'],
  slug: string
) => {
  try {
    // Get all stored keys from the state store
    const oidcKeys = await userStore.getAllKeys()

    if (oidcKeys.length === 0) {
      return null
    }

    let latestSession = null
    let latestTimestamp = 0

    // Loop through the OIDC keys and find the latest session
    for (const key of oidcKeys) {
      const sessionData = await userStore.get(key)
      const parsedSession = JSON.parse(sessionData)

      const createdTimestamp = parsedSession?.created

      if (
        parsedSession?.extraTokenParams?.slug === slug &&
        createdTimestamp &&
        createdTimestamp > latestTimestamp
      ) {
        latestTimestamp = createdTimestamp
        latestSession = parsedSession
      }
    }

    if (latestSession) {
      return latestSession
    } else {
      return null
    }
  } catch (error) {
    console.error('Error getting current OIDC session:', error)

    return null
  }
}

function base64URLEncode(str: ArrayBuffer): string {
  return btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

function generateRandomBytes(length: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(length))
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder()
  const data = encoder.encode(plain)

  return crypto.subtle.digest('SHA-256', data)
}

export async function generateCodeVerifier(): Promise<{
  code_verifier: string
  code_challenge: string
}> {
  // Generate random bytes for code_verifier
  const randomBytes = generateRandomBytes(32)
  const code_verifier = base64URLEncode(randomBytes.buffer)

  // Generate code_challenge from code_verifier
  const hashed = await sha256(code_verifier)
  const code_challenge = base64URLEncode(hashed)

  return {
    code_verifier,
    code_challenge
  }
}

export async function handleSSOLoginFromTokens(
  tokenResponse: any,
  handleSSOLogin: (user: Partial<SSOUser>) => Promise<void>
) {
  const idToken = tokenResponse.id_token
  const userInfo = jwtDecode(idToken) as UserProfile
  const userManager = getSSOUserManager()

  if (!userManager) {
    return
  }

  const expires_in = tokenResponse.expires_in || 3600
  const expires_at = Math.floor(Date.now() / 1000) + expires_in

  const user = new User({
    id_token: tokenResponse.id_token,
    access_token: tokenResponse.access_token,
    refresh_token: tokenResponse.refresh_token,
    token_type: tokenResponse.token_type,
    scope: tokenResponse.scope,
    session_state: tokenResponse.session_state || null,
    profile: userInfo,
    expires_at
  })

  await userManager.storeUser(user)
  await handleSSOLogin(user as Partial<SSOUser>)
}

export async function exchangeCodeForTokens(
  details: SSOCallbackRequest
): Promise<SSOCallbackResponse> {
  const SSO_CALLBACK_URL =
    getConfig().CUSTOMER_PORTAL_API_URL + '/v2/portal/public/sso/callback'

  const response = await CapacitorHttp.post({
    url: SSO_CALLBACK_URL,
    headers: {
      'Content-Type': 'application/json'
    },
    data: details
  })

  return response.data
}

export async function exchangeCodeManually({
  code,
  redirectUri,
  clientId
}: {
  code: string
  redirectUri: string
  clientId: string
}) {
  const userManager = getSSOUserManager()
  const { slug } = userManager.settings.extraTokenParams
  const latestSession = await getCurrentOidcSession(
    userManager.settings.userStore,
    slug as string
  )

  const codeVerifier = latestSession?.code_verifier
  const tokenEndpoint = userManager.settings.extraTokenParams.token_endpoint

  const body = {
    client_id: clientId,
    code: code,
    redirect_uri: redirectUri,
    grant_type: 'authorization_code',
    code_verifier: codeVerifier
  }

  const response = await CapacitorHttp.post({
    url: tokenEndpoint as string,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    data: body
  })

  let responseData

  try {
    responseData = await response.data
  } catch (parsingError) {
    console.error('Parsing error from token response:', parsingError)
  }

  if (response.status < 200 || response.status >= 300) {
    throw new Error(
      `Microsoft token exchange failed: ${JSON.stringify(responseData)}`
    )
  }

  return responseData
}

export async function initiateSSOFlow(
  provider: ProviderPublicConfig,
  handleSSOLogin: (user: Partial<SSOUser>) => Promise<void>
) {
  initializeSSOUserManager(provider)
  const userManager = getSSOUserManager()

  if (!userManager) {
    console.error('User manager not found')

    return
  }

  if (
    userManager.settings.extraTokenParams.slug === 'apple' &&
    getPlatformConfig().platform === 'ios'
  ) {
    // Dynamically import SignInWithApple only when needed
    const { SignInWithApple } = await import(
      '@capacitor-community/apple-sign-in'
    )
    const result = await SignInWithApple.authorize({
      clientId: userManager.settings.client_id,
      redirectURI: userManager.settings.redirect_uri,
      scopes: userManager.settings.scope
    })

    const { authorizationCode } = result.response
    const tokenEndpoint = userManager.settings.extraTokenParams.token_endpoint

    if (!tokenEndpoint) {
      throw new Error('Token endpoint is required but not found in settings')
    }

    let latestSession = await getCurrentOidcSession(
      userManager.settings.userStore,
      userManager.settings.extraTokenParams.slug as string
    )

    if (!latestSession) {
      const { code_verifier, code_challenge } = await generateCodeVerifier()

      latestSession = {
        code_verifier,
        code_challenge,
        redirect_uri: userManager.settings.redirect_uri,
        client_id: userManager.settings.client_id,
        ...(userManager.settings.client_secret
          ? { client_secret: userManager.settings.client_secret }
          : {}),
        created: Date.now(),
        extraTokenParams: userManager.settings.extraTokenParams
      }

      await userManager.settings.userStore.set(
        `oidc.session.${Date.now()}`,
        JSON.stringify(latestSession)
      )
    }

    const tokens = await exchangeCodeForTokens({
      token_endpoint: tokenEndpoint as string,
      grant_type: 'authorization_code',
      code: authorizationCode,
      redirect_uri: userManager.settings.redirect_uri,
      client_id: userManager.settings.client_id,
      ...(userManager.settings.client_secret
        ? { client_secret: userManager.settings.client_secret }
        : {}),
      code_verifier: latestSession?.code_verifier
    })

    await handleSSOLoginFromTokens(tokens, handleSSOLogin)
  } else {
    if (
      getPlatformConfig().platform === 'ios' ||
      getPlatformConfig().platform === 'android'
    ) {
      await userManager.signinRedirect({
        extraQueryParams: {
          display: 'touch',
          redirectTarget: 'self'
        }
      })
    } else {
      await userManager.signinRedirect()
    }
  }
}

export const handleAuthCodeFlow = async (provider: ProviderPublicConfig) => {
  const state = generateState()
  const { code_challenge, code_verifier } = await generateCodeVerifier()

  localStorage.setItem('sso_code_verifier', code_verifier)
  localStorage.setItem('sso_state', state)

  // redirect to provider's oidc_issuer with the following params
  const urlToNavigate = `${provider.oidc_config.oidc_issuer}?client_id=${
    provider.oidc_config.client_id
  }&redirect_uri=${
    provider.oidc_config.redirect_uri
  }&response_type=code&scope=${provider.oidc_config.scope
    .split(' ')
    .join(
      '+'
    )}&state=${state}&code_challenge=${code_challenge}&code_challenge_method=S256`

  window.location.href = urlToNavigate
}

export const generateState = (): string => {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  )
}
